import { FC, Fragment, ReactElement, ReactNode, memo } from 'react'

import { PortalName } from 'commons/types/enums'
import isObject from 'lodash/isObject'
import { CircularProgress } from 'ui/components'

import { AuthContext } from './AuthContext'
import { IdleWarningDialog } from './components/IdleWarningDialog'
import * as SC from './components/style'
import { useSmartRedirect } from './hooks/router'
import { useLogoutAuth, useManageUserSession } from './hooks/user'
import { SessionInfo } from './types/DTO'

interface AuthContainerProps {
  themeName: string
  portalName: PortalName
  children: ReactNode
  backgroundElement: ReactElement
  logoElement: ReactElement
  idleTimeoutInSeconds?: number
  footer?: ReactNode
  sessionInfo: SessionInfo
}

const Container: FC<AuthContainerProps> = memo(
  ({ themeName, sessionInfo, children, portalName, backgroundElement, logoElement, footer }) => {
    const { session, isFetching } = sessionInfo
    const { logout } = useLogoutAuth({ portalName })

    // check user activity and prolongation session
    // notion -> frontend: Auth flow
    const { isDialogShowed: isShowIdlePrompt, onSessionExtend } = useManageUserSession({ portalName })

    // references:
    // eslint-disable-next-line max-len
    // https://github.com/HeliosCompliance/orbit-auth/blob/master/src/hooks/helpers/saveTokenAndRedirectToProperPage.ts#L15
    // eslint-disable-next-line max-len
    // https://github.com/HeliosCompliance/orbit-auth/blob/b2a601320f5f29ec0a799421088b39e854231600/src/hooks/router/useSmartRedirect.ts#L16
    const isUserFetching = !session && isFetching
    const hasSession = session && isObject(session)
    const contextValue = { themeName, portalName, logout, hasSession, backgroundElement, logoElement, footer }

    return (
      <AuthContext.Provider value={contextValue}>
        {isUserFetching && (
          <SC.SpinnerContainer>
            <CircularProgress centered />
          </SC.SpinnerContainer>
        )}
        {!isFetching && (
          <>
            <Fragment key={session ? session.expires_at : 'not-logged'}>{children}</Fragment>
            {hasSession && isShowIdlePrompt && (
              <IdleWarningDialog hasUser={hasSession} onContinue={onSessionExtend} onLogout={() => logout({})} />
            )}
          </>
        )}
      </AuthContext.Provider>
    )
  }
)

// took this hook out of AuthContainer due to it often causes rerenders of all subtree
export const AuthContainer = (props) => {
  // Detect any route changes and page load to move unauthorized user to login page,
  // or move user with restricted rights to proper route.
  const sessionInfo = useSmartRedirect(props.portalName)

  return <Container sessionInfo={sessionInfo} {...props} />
}
